<template>
  <div class="admin-landing-page-show p-4 sm:p-6 lg:p-8">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">{{ page.title }}</h1>
        <p class="text-gray-600 mt-1">Landing page details and analytics</p>
      </div>
      <div class="flex items-center gap-2">
        <Link
          :href="route('admin.landing-pages.index')"
          class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Back to List
        </Link>
        <a
          :href="route('landing-page.view', page.slug)"
          target="_blank"
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
        >
          View Live
          <icon name="arrow-right" class="w-4 h-4" />
        </a>
        <button
          @click="deletePage"
          class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Delete
        </button>
      </div>
    </div>

    <!-- Page Information -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Main Details -->
      <div class="lg:col-span-2 space-y-6">
        <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">Page Information</h2>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <p class="text-gray-900">{{ page.title }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <code class="px-3 py-2 bg-gray-100 rounded text-sm">{{ page.slug }}</code>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <span
                :class="[
                  'inline-block px-3 py-1 text-sm font-medium rounded-full',
                  page.is_published
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                ]"
              >
                {{ page.is_published ? 'Published' : 'Draft' }}
              </span>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Sections</label>
              <p class="text-gray-900">{{ page.sections ? page.sections.length : 0 }} sections</p>
            </div>
          </div>
        </div>

        <div v-if="page.sections && page.sections.length > 0" class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">Sections</h2>
          <div class="space-y-2">
            <div
              v-for="(section, index) in page.sections"
              :key="index"
              class="p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-gray-700">{{ section.type || 'Unknown' }}</span>
                <span class="text-xs text-gray-500">Section {{ index + 1 }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="space-y-6">
        <!-- Analytics -->
        <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">Analytics</h2>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Total Views</label>
              <p class="text-2xl font-bold text-green-600">{{ page.views || 0 }}</p>
            </div>
          </div>
        </div>

        <!-- User Information -->
        <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">Page Owner</h2>
          <div class="space-y-2" v-if="page.user">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <p class="text-gray-900">{{ page.user.name }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <a :href="'mailto:' + page.user.email" class="text-green-600 hover:text-green-800 hover:underline">
                {{ page.user.email }}
              </a>
            </div>
          </div>
          <p v-else class="text-gray-500 text-sm">Guest user</p>
        </div>

        <!-- Dates -->
        <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">Dates</h2>
          <div class="space-y-2">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Created</label>
              <p class="text-gray-900 text-sm">{{ page.created_at }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
              <p class="text-gray-900 text-sm">{{ page.updated_at }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon.vue'
import Layout from '@/Shared/Layout'

export default {
  layout: Layout,
  components: {
    Link,
    Icon,
  },
  props: {
    page: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: 'Landing Page Details',
    },
  },
  methods: {
    deletePage() {
      if (confirm('Are you sure you want to delete this landing page? This action cannot be undone.')) {
        router.delete(route('admin.landing-pages.destroy', this.page.id), {
          onSuccess: () => {
            router.visit(route('admin.landing-pages.index'))
            if (window.$toast) {
              window.$toast.success('Landing page deleted successfully')
            }
          },
        })
      }
    },
  },
}
</script>

<style scoped>
.admin-landing-page-show {
  min-height: 100vh;
  background-color: #f9fafb;
}
</style>



